import { computed, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { API } from "@/services/apiInstance";
import { operationalFreshness } from "./queryFreshness";

const REFERENCE_STALE_TIME_MS = 5 * 60 * 1000;

export const userKeys = {
    all: ["users"],
    list: () => ["users", "list"],
    statuses: (params = {}) => ["users", "statuses", params],
    detail: (id) => ["users", "detail", id]
};

export const locationKeys = {
    all: ["locations"],
    list: () => ["locations", "list"],
    detail: (id) => ["locations", "detail", id]
};

export const departmentKeys = {
    all: ["departments"],
    list: () => ["departments", "list"],
    detail: (id) => ["departments", "detail", id]
};

function referenceQuery(queryKey, queryFn, options = {}) {
    return useQuery({
        queryKey,
        queryFn,
        staleTime: REFERENCE_STALE_TIME_MS,
        ...options
    });
}

export function useUsers(options = {}) {
    return referenceQuery(userKeys.list(), async () => (await API.User.List()) || [], options);
}

export function useUserStatuses(params = {}, options = {}) {
    return referenceQuery(
        userKeys.statuses(params),
        async ({ signal }) => (await API.User.ListStatuses(params, { signal })) || [],
        options
    );
}

export function useUser(idRef) {
    return useQuery({
        queryKey: computed(() => userKeys.detail(unref(idRef) || "")),
        queryFn: () => API.User.GetByID(unref(idRef)),
        enabled: computed(() => Boolean(unref(idRef))),
        ...operationalFreshness
    });
}

// Profile belongs to the authenticated account, including on shared tablets
// where the operational staff selector points at another person.
export function useCurrentUser(idRef) {
    return useQuery({
        queryKey: computed(() => userKeys.detail(unref(idRef) || "")),
        queryFn: () => API.User.GetMe(),
        enabled: computed(() => Boolean(unref(idRef))),
        ...operationalFreshness
    });
}

export function useLocations(options = {}) {
    return referenceQuery(
        locationKeys.list(),
        async () => (await API.Location.List()) || [],
        options
    );
}

export function useDepartments(options = {}) {
    return referenceQuery(
        departmentKeys.list(),
        async () => (await API.Department.List()) || [],
        options
    );
}
